import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import WindroosSpinner from "../../components/WindroosSpinner";
import useSignedUrl from "../../utils/useSignedUrl";
import {
  getMediaFallbackUrl,
  reportFallbackInUse,
} from "../../utils/mediaFallbacks";
import { reportAppError } from "../../utils/observability";
import { getResponsiveImageKeys } from "../../utils/responsiveImages";

function logImageFailure(s3Key, reason) {
  reportAppError({
    stage: "image_render",
    errorCode: reason.includes("fallback") ? "fallback" : "render",
    mediaCategory: "image",
  });
  if (typeof window === "undefined") return;
  const host = window.location.hostname;
  const shouldLog =
    process.env.NODE_ENV === "development" ||
    host === "windroos.boss-tech.nl" ||
    host === "localhost" ||
    host === "127.0.0.1";
  if (!shouldLog) return;
  console.error(`[Windroos media] image render failed (${s3Key})`, { s3Key, reason });
}

function FallbackBox({ sx }) {
  return (
    <Box
      aria-hidden="true"
      sx={{
        background:
          "radial-gradient(circle at 32% 28%, rgba(136, 205, 211, 0.48), transparent 34%), linear-gradient(135deg, #FFFAF4, #E7F5F7)",
        width: "100%",
        height: "100%",
        minHeight: 140,
        border: "1px solid rgba(60, 143, 153, 0.2)",
        ...sx,
      }}
      data-source="placeholder"
    />
  );
}

function LoadingMediaBox({ sx, children }) {
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: 140,
        backgroundColor: "#FFFAF4",
        overflow: "hidden",
        ...sx,
      }}
    >
      {children}
      <Skeleton
        variant="rectangular"
        animation="wave"
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          minHeight: "inherit",
          transform: "none",
          backgroundColor: "rgba(255, 250, 244, 0.82)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "grid",
          placeItems: "center",
          background:
            "linear-gradient(135deg, rgba(255,250,244,0.58), rgba(231,245,247,0.62))",
        }}
      >
        <WindroosSpinner label={null} size={42} subtle />
      </Box>
    </Box>
  );
}

export default function SignedImage({
  s3Key,
  alt = "",
  sx = {},
  objectFit = "cover",
  responsiveSizes = "(max-width: 600px) 100vw, 50vw",
  ...rest
}) {
  const { url, error: urlError, status } = useSignedUrl(s3Key);
  const responsiveKeys = getResponsiveImageKeys(s3Key);
  const smallAsset = useSignedUrl(responsiveKeys?.small || "");
  const largeAsset = useSignedUrl(responsiveKeys?.large || "");
  const fallbackUrl = getMediaFallbackUrl(s3Key);
  const [sourceState, setSourceState] = useState("signed");
  const [responsiveDisabled, setResponsiveDisabled] = useState(false);
  const [finalError, setFinalError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    setSourceState("signed");
    setResponsiveDisabled(false);
    setFinalError(false);
    setImageLoaded(false);
  }, [s3Key]);

  const signedUrlReady = status === "ready" && url && !urlError;
  const usingFallback = Boolean(fallbackUrl) && (sourceState === "fallback" || urlError);
  const effectiveUrl = usingFallback ? fallbackUrl : signedUrlReady ? url : null;
  const responsiveSettled = [smallAsset.status, largeAsset.status].every(
    (assetStatus) => assetStatus === "ready" || assetStatus === "error"
  );
  const responsiveSourceActive = Boolean(
    responsiveKeys && smallAsset.url && largeAsset.url && !responsiveDisabled
  );

  if (!s3Key) return null;
  if ((urlError && !fallbackUrl) || finalError) return <FallbackBox sx={sx} />;

  if (urlError && fallbackUrl) {
    reportFallbackInUse(s3Key, "signed-url-request-failed");
  }

  if (!effectiveUrl || (responsiveKeys && !responsiveSettled)) {
    return <LoadingMediaBox sx={sx} />;
  }

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: 140,
        overflow: "hidden",
        backgroundColor: "#FFFAF4",
        ...sx,
      }}
    >
      {!imageLoaded && <LoadingMediaBox sx={{ position: "absolute", inset: 0 }} />}
      <Box component="picture" sx={{ display: "contents" }}>
        {responsiveSourceActive && (
          <source
            type="image/webp"
            srcSet={`${smallAsset.url} 480w, ${largeAsset.url} 960w`}
            sizes={responsiveSizes}
          />
        )}
        <Box
          component="img"
          src={effectiveUrl}
          alt={alt}
          loading="lazy"
          decoding="async"
          data-source={usingFallback ? "fallback" : responsiveSourceActive ? "responsive" : "signed"}
          data-s3-key={s3Key}
          onLoad={() => setImageLoaded(true)}
          onError={() => {
            if (responsiveSourceActive) {
              setResponsiveDisabled(true);
              setImageLoaded(false);
              logImageFailure(s3Key, "responsive-img-onerror-using-original");
              return;
            }
            if (sourceState === "signed" && fallbackUrl) {
              setSourceState("fallback");
              setImageLoaded(false);
              reportFallbackInUse(s3Key, "signed-img-onerror");
              logImageFailure(s3Key, "signed-img-onerror-using-legacy-fallback");
              return;
            }
            setFinalError(true);
            logImageFailure(s3Key, "img-onerror-final");
          }}
          sx={{
            display: "block",
            width: "100%",
            height: "100%",
            objectFit,
            opacity: imageLoaded ? 1 : 0,
            transition: "opacity 160ms ease",
          }}
          {...rest}
        />
      </Box>
    </Box>
  );
}
